import type { MouseEvent, ReactNode } from 'react';
import type { FilterValue } from './payloadQuery';

const INDENT = '  ';

/** Called when the user clicks a value. `anchor` is the click position, used
 *  to place the operator menu for numbers. */
export type JsonPick = (path: string, value: FilterValue, anchor: { x: number; y: number }) => void;

export interface JsonRenderOptions {
  /** Makes every leaf value clickable. Omit for a read-only render. */
  onPick?: JsonPick;
  /** Dotted path accumulated so far. Internal to the recursion. */
  path?: string;
}

/** Wrap a leaf value so clicking it builds a filter. Rendered as an inline
 *  button so it sits inside the <pre> without disturbing the layout. */
function Pickable({
  children,
  path,
  value,
  onPick,
}: {
  children: ReactNode;
  path: string;
  value: FilterValue;
  onPick?: JsonPick;
}) {
  if (!onPick || !path) return <>{children}</>;
  const handle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onPick(path, value, { x: e.clientX, y: e.clientY });
  };
  return (
    <button
      type="button"
      className="ds-json-pick"
      onClick={handle}
      title={`Filter by ${path}`}
      aria-label={`Filter by ${path} ${value === null ? 'null' : String(value)}`}
    >
      {children}
    </button>
  );
}

/** Render an arbitrary JSON value as syntax-highlighted React nodes.
 *  Classes (ds-json-*) are styled in index.css. When `opts.onPick` is given
 *  every leaf becomes a click target that turns into a filter chip. */
export function renderJson(value: unknown, depth = 0, opts: JsonRenderOptions = {}): ReactNode {
  const { onPick, path = '' } = opts;
  const leaf = (node: ReactNode, v: FilterValue) => (
    <Pickable path={path} value={v} onPick={onPick}>
      {node}
    </Pickable>
  );

  if (value === null) return leaf(<span className="ds-json-null">null</span>, null);
  if (typeof value === 'boolean')
    return leaf(<span className="ds-json-bool">{String(value)}</span>, value);
  if (typeof value === 'number') return leaf(<span className="ds-json-num">{value}</span>, value);
  if (typeof value === 'string') return leaf(<span className="ds-json-str">"{value}"</span>, value);

  const child = (key: string | number) => ({
    onPick,
    path: path ? `${path}.${key}` : String(key),
  });

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="ds-json-punc">[]</span>;
    const pad = INDENT.repeat(depth + 1);
    return (
      <>
        {'[\n'}
        {value.map((item, i) => (
          <span key={i}>
            {pad}
            {renderJson(item, depth + 1, child(i))}
            {i < value.length - 1 ? ',' : ''}
            {'\n'}
          </span>
        ))}
        {INDENT.repeat(depth)}]
      </>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="ds-json-punc">{'{}'}</span>;
    const pad = INDENT.repeat(depth + 1);
    return (
      <>
        {'{\n'}
        {entries.map(([k, v], i) => (
          <span key={k}>
            {pad}
            <span className="ds-json-key">"{k}"</span>
            {': '}
            {renderJson(v, depth + 1, child(k))}
            {i < entries.length - 1 ? ',' : ''}
            {'\n'}
          </span>
        ))}
        {INDENT.repeat(depth)}
        {'}'}
      </>
    );
  }

  return leaf(<span className="ds-json-str">{String(value)}</span>, String(value));
}
